import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card'; // Caminho correto para Card
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge as UiBadge } from '@/components/ui/badge';
import { toast } from 'react-hot-toast';
import { Loader2, PlusCircle, Edit, Trash2, CheckCircle, XCircle, CalendarDays, HelpCircle } from 'lucide-react';
import SpecialEventForm from './SpecialEventForm';
import { listSpecialEvents, deleteSpecialEvent } from '@/lib/gamification/missionService';
import { SpecialEvent, SpecialEventRow, SpecialEventConfig } from '@/lib/gamification/types';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Json } from '../../../types/supabase'; // Importando Json
import { useTheme } from '@/contexts/ThemeContext'; // Importar useTheme

interface SpecialEventsAdminProps {
  onOpenHelp: () => void;
}

const SpecialEventsAdmin: React.FC<SpecialEventsAdminProps> = ({ onOpenHelp }) => {
  const { theme } = useTheme(); // Usar o hook useTheme
  const [events, setEvents] = useState<SpecialEvent[]>([]); // Usar SpecialEvent[] que inclui config parseado
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<SpecialEventRow | null>(null); // Form espera SpecialEventRow
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSpecialEvents(supabase);
      setEvents(data);
    } catch (err: any) {
      setError('Falha ao carregar eventos especiais.');
      toast.error('Falha ao carregar eventos especiais.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDelete = async (eventId: string) => {
    toast((t) => (
      <div className={`flex flex-col items-center p-2 rounded-md shadow-lg ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-800'}`}>
        <p className="font-semibold text-center mb-2">Confirmar exclusão?</p>
        <p className={`text-sm mb-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Esta ação não pode ser desfeita.</p>
        <div className="flex w-full gap-2">
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const result = await deleteSpecialEvent(supabase, eventId);
                if (result.success) {
                  toast.success('Evento excluído!');
                  fetchEvents();
                } else {
                  throw result.error || new Error('Falha ao excluir evento');
                }
              } catch (error: any) {
                toast.error(error.message || 'Falha ao excluir evento.');
                console.error("Erro ao excluir evento:", error);
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Excluir
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={() => toast.dismiss(t.id)}>
            Cancelar
          </Button>
        </div>
      </div>
    ), { duration: 6000 });
  };

  const handleSaveEvent = (savedEvent: SpecialEvent) => {
    setShowForm(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const handleEdit = (event: SpecialEvent) => {
    // SpecialEventForm espera SpecialEventRow, então precisamos converter
    const eventRowData: SpecialEventRow = {
      ...event,
      config: event.config as unknown as Json, // config já é o objeto parseado, mas o form espera Json bruto
    };
    setEditingEvent(eventRowData);
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  if (showForm) {
    return (
      <SpecialEventForm
        initialData={editingEvent}
        onSave={handleSaveEvent}
        onCancel={() => { setShowForm(false); setEditingEvent(null); }}
        isLoading={isSubmitting}
      />
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (e) {
      return dateString; // Retorna a string original se a data for inválida
    }
  };

  return (
    <Card className={theme === 'dark' ? 'dark' : ''}>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Gerenciar Eventos Especiais</CardTitle>
          <CardDescription>
            Crie, edite ou remova eventos especiais (ex: bônus de XP, missões sazonais).
          </CardDescription>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={handleCreateNew}>
            <PlusCircle className="mr-2 h-4 w-4" /> Criar Novo Evento
          </Button>
          <Button variant="outline" size="icon" onClick={onOpenHelp} title="Ajuda Configuração JSON">
            <HelpCircle className="h-5 w-5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex justify-center items-center py-10">
            <Loader2 className={`h-8 w-8 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-primary'}`} />
          </div>
        )}
        {error && <p className={`text-center py-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`}>{error}</p>}
        {!loading && !error && events.length === 0 && <p className={`text-center py-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Nenhum evento especial cadastrado.</p>}
        {!loading && !error && events.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Chave</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Fim</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className={`font-mono text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{event.event_key}</TableCell>
                    <TableCell className="font-medium">{event.title}</TableCell>
                    <TableCell><UiBadge variant={theme === 'dark' ? 'default' : 'secondary'}>{event.event_type}</UiBadge></TableCell>
                    <TableCell className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{formatDate(event.start_date)}</TableCell>
                    <TableCell className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{formatDate(event.end_date)}</TableCell>
                    <TableCell>
                      {event.is_active ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="icon" onClick={() => handleEdit(event)}>
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Editar</span>
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => handleDelete(event.id)}>
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Excluir</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SpecialEventsAdmin; 